/**
 * 商品搭配規則 pairing-rule data hooks — Feat #2168 (G-2166-K-04), consuming
 * the #2167 mock-first contract (mocks/handlers/pairingRule.ts):
 * - GET  /products/:id/pairing-rules   (active AND inactive; UI filters)
 * - POST /products/:id/pairing-rules
 * - PATCH /pairing-rules/:id           (deactivate-only lifecycle — no DELETE)
 *
 * Kit/combo = pairing rules between EXISTING SKUs on canvas 1:2 (0:1
 * Sept - SO PO) — never the 16:3396 OEM canvas grouping (G-2166-K-06).
 * MOCK SEAM: products CU-1 is not flipped, so nothing here claims a live
 * :8090 leg (GAP-REPORT §4 seam honesty).
 *
 * ROUTED THROUGH `pairingRuleApi` (2026-09-16). This file used to call
 * `apiClient` directly, with that promotion recorded as a followUp for the
 * service owner — and a followUp in a docblock is invisible to every gate.
 * The products-surface census walks this file (it is in the router's import
 * closure) and counted four transport call sites outside the seam with no
 * register row. The followUp is discharged rather than registered: the
 * transport lives in `src/services/api/pairingRule.api.ts` and this hook keeps
 * the SWR lifecycle, which is the half it actually owns.
 *
 * Mutations follow the house SWR-invalidation law: await the API call,
 * clear the cache, THEN return — the caller's refresh() fetches fresh data.
 */

import { useMemo } from 'react';
import { clearCache, useRequest } from 'ahooks';
import { pairingRuleApi } from '@/services/api/pairingRule.api';
import type {
  CreateProductPairingRuleRequest,
  PairingPricingMode,
  PairingType,
  ProductPairingRule,
  UpdateProductPairingRuleRequest,
} from '@/types/api/pairingRule';
import type { NetSuiteAvailabilityRow } from '@/types/api/purchaseOrder';

// ---------------------------------------------------------------------------
// Module surface shared by the section + modal (label keys as DATA — full
// literal keys so i18n:audit sees them; TAB_LABEL_KEYS precedent)
// ---------------------------------------------------------------------------

// O1 is OPEN: no pairing pricing mode is chosen or wired — there is
// deliberately NO default-mode constant here. A rule's pricingMode stays null
// (undecided, pending 林芊/Judy); the six enum values are data-ready only.
export const PAIRING_TYPE_LABEL_KEYS: Record<PairingType, string> = {
  mandatory: 'products:pairing.types.mandatory',
  optional: 'products:pairing.types.optional',
};

export const PAIRING_PRICING_MODE_LABEL_KEYS: Record<PairingPricingMode, string> = {
  bundled_into_base: 'products:pairing.pricingModes.bundled_into_base',
  free: 'products:pairing.pricingModes.free',
  separate_line: 'products:pairing.pricingModes.separate_line',
  fixed_amount: 'products:pairing.pricingModes.fixed_amount',
  fixed_discount: 'products:pairing.pricingModes.fixed_discount',
  pct_discount: 'products:pairing.pricingModes.pct_discount',
};

// ---------------------------------------------------------------------------
// Cache keys (static, /api/-prefixed — house rule)
// ---------------------------------------------------------------------------

export const pairingRulesCacheKey = (productId: string) =>
  `/api/products/${productId}/pairing-rules`;

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export function usePairingRules(productId?: string) {
  const { data, error, loading, run } = useRequest(
    async () => pairingRuleApi.list(productId as string),
    {
      ready: !!productId,
      refreshDeps: [productId],
      cacheKey: productId ? pairingRulesCacheKey(productId) : undefined,
    }
  );

  return {
    /** All rules of the parent — active AND inactive (inactive stays visible). */
    rules: data ?? [],
    error,
    isLoading: loading,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      if (productId) clearCache(pairingRulesCacheKey(productId));
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// Mutations (await → invalidate → return; deactivate-only lifecycle)
// ---------------------------------------------------------------------------

export function usePairingRuleMutations(productId?: string) {
  const invalidate = () => {
    if (productId) clearCache(pairingRulesCacheKey(productId));
  };

  const createRule = async (
    body: CreateProductPairingRuleRequest
  ): Promise<ProductPairingRule> => {
    const rule = await pairingRuleApi.create(productId as string, body);
    invalidate();
    return rule;
  };

  const updateRule = async (
    ruleId: string,
    patch: UpdateProductPairingRuleRequest
  ): Promise<ProductPairingRule> => {
    const rule = await pairingRuleApi.update(ruleId, patch);
    invalidate();
    return rule;
  };

  /** Retirement path — PATCH isActive (there is deliberately NO DELETE). */
  const setRuleActive = (ruleId: string, isActive: boolean): Promise<ProductPairingRule> =>
    updateRule(ruleId, { isActive });

  return { createRule, updateRule, setRuleActive };
}

// ---------------------------------------------------------------------------
// Component 可供量 read-through (G-2166-K-03 indicator input)
// ---------------------------------------------------------------------------

/**
 * Batched Tier-1 availability read-through for the rules' COMPONENT products
 * (`GET /netsuite/availability?productIds=…`). Read-only echo — the indicator
 * threshold itself is the component's Tier-2 `planning.reorderPoint` registry
 * field (PLAN diamond 2), never the Tier-1 echo. No cacheKey: the id set is
 * per-parent-product (dynamic-key leak guard — VendorLinesSection precedent).
 * Absent data stays absent: consumers render NO indicator, never a fabricated
 * quantity.
 */
export function usePairingComponentAvailability(componentProductIds: readonly string[]) {
  const idsKey = useMemo(
    () => [...new Set(componentProductIds)].sort().join(','),
    [componentProductIds]
  );

  const { data, loading } = useRequest(
    async () => {
      const rows = await pairingRuleApi.componentAvailability(idsKey);
      return new Map<string, NetSuiteAvailabilityRow>(rows.map((row) => [row.productId, row]));
    },
    { refreshDeps: [idsKey], ready: idsKey.length > 0 }
  );

  return {
    /** productId → availability row; undefined until the read-through lands. */
    availabilityByProductId: data,
    isLoading: loading,
  };
}
